import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemText,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import categoryService from '../../../services/categoryService';

const CategoryDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const [category, setCategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategory();
    fetchSubcategories();
  }, [id]);

  const fetchCategory = async () => {
    try {
      setLoading(true);
      const data = await categoryService.getCategoryById(id);
      setCategory(data);
    } catch (error) {
      toast.error(t('categories.fetchError'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubcategories = async () => {
    try {
      const data = await categoryService.getSubcategories(id);
      setSubcategories(data);
    } catch (error) {
      console.error('Failed to fetch subcategories:', error);
    }
  };

  if (loading) {
    return <Box sx={{ p: 3 }}>{t('common.loading')}</Box>;
  }

  if (!category) {
    return <Box sx={{ p: 3 }}>{t('common.noData')}</Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/categories')}
          >
            {t('common.back')}
          </Button>
          <Typography variant="h4">{t('categories.viewCategory')}</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/categories/edit/${id}`)}
        >
          {t('categories.editCategory')}
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                {category.imageUrl ? (
                  <Avatar
                    src={category.imageUrl}
                    variant="rounded"
                    sx={{ width: 100, height: 100, mr: 2 }}
                  >
                    <CategoryIcon sx={{ fontSize: 50 }} />
                  </Avatar>
                ) : (
                  <Avatar
                    variant="rounded"
                    sx={{ width: 100, height: 100, mr: 2, bgcolor: 'primary.main' }}
                  >
                    <CategoryIcon sx={{ fontSize: 50 }} />
                  </Avatar>
                )}
                <Box>
                  <Typography variant="h5">{category.name}</Typography>
                  {category.parentName && (
                    <Chip label={`Parent: ${category.parentName}`} size="small" color="info" sx={{ mt: 1 }} />
                  )}
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.description')}
                  </Typography>
                  <Typography variant="body1">
                    {category.description || 'No description'}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.status')}
                  </Typography>
                  <Typography variant="body1">
                    <Chip
                      label={category.isActive ? t('common.active') : t('common.inactive')}
                      color={category.isActive ? 'success' : 'default'}
                      size="small"
                    />
                    {' '}
                    <Chip
                      label={category.sellOnline === false ? t('sellOnline.offline') : t('sellOnline.online')}
                      color={category.sellOnline === false ? 'default' : 'info'}
                      variant="outlined"
                      size="small"
                    />
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Display Order
                  </Typography>
                  <Typography variant="body1">{category.displayOrder || 0}</Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Subcategories
                  </Typography>
                  <Typography variant="body1">
                    <Chip label={category.subcategoryCount || 0} color="secondary" size="small" />
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    {t('categories.productsCount')}
                  </Typography>
                  <Typography variant="body1">
                    <Chip label={category.productCount || 0} color="primary" size="small" />
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.createdAt')}
                  </Typography>
                  <Typography variant="body2">
                    {new Date(category.createdAt).toLocaleString()}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.updatedAt')}
                  </Typography>
                  <Typography variant="body2">
                    {new Date(category.updatedAt).toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Subcategories */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Subcategories ({subcategories.length})
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {subcategories.length > 0 ? (
                <List>
                  {subcategories.map((subcategory) => (
                    <ListItem
                      key={subcategory.id}
                      button
                      onClick={() => navigate(`/categories/view/${subcategory.id}`)}
                    >
                      <ListItemText
                        primary={subcategory.name}
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">
                              {subcategory.description || 'No description'}
                            </Typography>
                            <Box sx={{ mt: 0.5, display: 'flex', gap: 1 }}>
                              <Chip
                                label={`${subcategory.productCount || 0} products`}
                                size="small"
                                color="primary"
                              />
                              <Chip
                                label={subcategory.isActive ? t('common.active') : t('common.inactive')}
                                size="small"
                                color={subcategory.isActive ? 'success' : 'default'}
                              />
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('common.noData')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Products */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('categories.productsCount')} ({category.productCount || 0})
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Alert severity="info">
                Products module coming soon. This category has {category.productCount || 0} products.
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CategoryDetails;
